import { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Spinner,
  Collapse,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import api from "../helpers/api";

const CreateRoleModal = ({
  isOpen,
  toggle,
  onSuccess,
  groupedPermissions,
  editMode = false,
  roleData = null,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    description: "",
    permissionIds: [],
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (editMode && roleData) {
        setFormData({
          name: roleData.name || "",
          displayName: roleData.displayName || "",
          description: roleData.description || "",
          permissionIds: roleData.permissions?.map((p) => p.id) || [],
        });
      } else {
        setFormData({
          name: "",
          displayName: "",
          description: "",
          permissionIds: [],
        });
      }
      setErrors({});
      setApiError("");
      // Expand all groups by default
      const groups = {};
      if (Array.isArray(groupedPermissions)) {
        groupedPermissions.forEach((group) => {
          groups[group.resource] = true;
        });
      }
      setExpandedGroups(groups);
    }
  }, [isOpen, editMode, roleData, groupedPermissions]);

  // Format resource name for display
  const formatResourceName = (resource) => {
    return resource
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Toggle permission selection
  const togglePermission = (permissionId) => {
    setFormData((prev) => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permissionId)
        ? prev.permissionIds.filter((id) => id !== permissionId)
        : [...prev.permissionIds, permissionId],
    }));
  };

  // Select/Deselect all permissions in a group
  const toggleSelectAll = (resource, permissions) => {
    const permissionIds = permissions.map((p) => p.id);
    const allSelected = permissionIds.every((id) =>
      formData.permissionIds.includes(id)
    );

    setFormData((prev) => ({
      ...prev,
      permissionIds: allSelected
        ? prev.permissionIds.filter((id) => !permissionIds.includes(id))
        : [...new Set([...prev.permissionIds, ...permissionIds])],
    }));
  };

  // Toggle group expansion
  const toggleGroup = (resource) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [resource]: !prev[resource],
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.displayName?.trim()) {
      newErrors.displayName = "Role name is required";
    }

    if (formData.permissionIds.length === 0) {
      newErrors.permissions = "At least one permission must be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate role name from display name (sanitized)
  const generateRoleName = (displayName) => {
    const sanitized = displayName
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '')
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, '');
    return "ROLE_" + (sanitized || "UNNAMED");
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: generateRoleName(formData.displayName),
        displayName: formData.displayName,
        description: formData.description,
        permissionIds: formData.permissionIds,
      };

      if (editMode && roleData?.id) {
        await api.put(`rbac/roles/${roleData.id}`, payload);
      } else {
        await api.post("rbac/roles", payload);
      }

      onSuccess();
    } catch (error) {
      console.error("Error saving role:", error);
      setApiError(
        error?.response?.data?.message || "Failed to save role. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Count selected permissions in a group
  const getSelectedCount = (permissions) => {
    return permissions.filter((p) => formData.permissionIds.includes(p.id)).length;
  };

  // Handle modal close with dirty check
  const handleClose = () => {
    const isDirty = formData.displayName || formData.description || formData.permissionIds.length > 0;
    if (isDirty && !window.confirm("You have unsaved changes. Are you sure you want to close?")) {
      return;
    }
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={isSubmitting ? undefined : handleClose}
      size="xl"
      scrollable
      backdrop={isSubmitting ? "static" : true}
      keyboard={!isSubmitting}
    >
        <ModalHeader toggle={isSubmitting ? undefined : handleClose}>
          <FeatherIcon icon="shield" className="me-2" size={20} />
          {editMode ? "Edit Role" : "Create New Role"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            {/* Role Name */}
            <Row className="mb-4">
              <label className="col-sm-3 col-form-label">
                Role Name <span className="text-danger">*</span>
              </label>
              <Col sm={9}>
                <input
                  type="text"
                  className={`form-control ${errors.displayName ? "is-invalid" : ""}`}
                  placeholder="Enter role name (e.g., Technician Lead)"
                  value={formData.displayName}
                  onChange={(e) =>
                    setFormData({ ...formData, displayName: e.target.value })
                  }
                />
                {errors.displayName && (
                  <small className="text-danger">{errors.displayName}</small>
                )}
                {formData.displayName && (
                  <small className="text-muted d-block mt-1">
                    System name: {generateRoleName(formData.displayName)}
                  </small>
                )}
              </Col>
            </Row>

            {/* Description */}
            <Row className="mb-4">
              <label className="col-sm-3 col-form-label">Description</label>
              <Col sm={9}>
                <textarea
                  className="form-control"
                  placeholder="Enter role description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </Col>
            </Row>

            {/* Permissions Section */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <label className="form-label mb-0 fw-bold">
                  Permissions <span className="text-danger">*</span>
                </label>
                <span className="text-muted">
                  {formData.permissionIds.length} selected
                </span>
              </div>
              {errors.permissions && (
                <div className="alert alert-danger py-2 mb-3">
                  {errors.permissions}
                </div>
              )}

              {!Array.isArray(groupedPermissions) || groupedPermissions.length === 0 ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Loading permissions...</p>
                </div>
              ) : (
                <div className="border rounded">
                  {groupedPermissions.map((group) => {
                      const permissions = group.permissions || [];
                      const selectedCount = getSelectedCount(permissions);
                      const allSelected = permissions.length > 0 && selectedCount === permissions.length;
                      const isExpanded = expandedGroups[group.resource];

                      return (
                        <Card key={group.resource} className="mb-0 border-0 border-bottom">
                          <CardHeader
                            className="d-flex justify-content-between align-items-center py-2 px-3 bg-light"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleGroup(group.resource)}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <FeatherIcon
                                icon={isExpanded ? "chevron-down" : "chevron-right"}
                                size={16}
                              />
                              <span className="fw-medium">
                                {group.resourceDisplayName || formatResourceName(group.resource)}
                              </span>
                              <span className="badge bg-secondary">
                                {selectedCount}/{permissions.length}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              color="link"
                              className="p-0 text-decoration-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSelectAll(group.resource, permissions);
                              }}
                            >
                              {allSelected ? "Deselect All" : "Select All"}
                            </Button>
                          </CardHeader>
                          <Collapse isOpen={isExpanded}>
                            <CardBody className="py-2 px-3">
                              <Row>
                                {permissions.map((permission) => (
                                  <Col md={6} lg={4} key={permission.id}>
                                    <div className="form-check mb-2">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`perm-${permission.id}`}
                                        checked={formData.permissionIds.includes(
                                          permission.id
                                        )}
                                        onChange={() =>
                                          togglePermission(permission.id)
                                        }
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor={`perm-${permission.id}`}
                                      >
                                        <span className="fw-medium">
                                          {permission.action}
                                        </span>
                                        {permission.displayName && (
                                          <small className="text-muted d-block">
                                            {permission.displayName}
                                          </small>
                                        )}
                                      </label>
                                    </div>
                                  </Col>
                                ))}
                              </Row>
                            </CardBody>
                          </Collapse>
                        </Card>
                      );
                    }
                  )}
                </div>
              )}
            </div>

            {/* Error Message */}
            {apiError && <div className="alert alert-danger">{apiError}</div>}
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="success"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {editMode ? "Updating..." : "Creating..."}
              </>
            ) : editMode ? (
              "Update Role"
            ) : (
              "Create Role"
            )}
          </Button>
        </ModalFooter>
    </Modal>
  );
};

export default CreateRoleModal;
